import { useState } from "react";
import type { Currency } from "../entities/ledger/types";
import { planningMonth } from "../features/planning/api";
import { BudgetsPanel } from "../features/planning/BudgetsPanel";
import { PlanGroupsPanel } from "../features/planning/PlanGroupsPanel";
import { PlanItemsPanel } from "../features/planning/PlanItemsPanel";
import { PlanningFxPanel } from "../features/planning/PlanningFxPanel";
import { PlanningSummaryCards } from "../features/planning/PlanningSummary";
import { usePlanning } from "../features/planning/usePlanning";
import { useDashboard } from "../features/workspace/useDashboard";
import { ErrorPanel, LoadingPanel } from "../shared/ui/PageState";
import { SectionTabs } from "../shared/ui/SectionTabs";

export type PlanningView = "items" | "groups" | "budgets" | "rates";

interface PlanningPageProps {
  view: PlanningView;
  currency: Currency;
  onNotify: (message: string) => void;
}

const TABS = [
  { to: "/planning", label: "Plan items", end: true },
  { to: "/planning/groups", label: "Groups" },
  { to: "/planning/budgets", label: "Budgets" },
  { to: "/planning/rates", label: "Planning rates" },
];

const LEDE: Record<PlanningView, string> = {
  groups: "A group composes plan items. It holds no amount of its own, and a transaction assigned to it is never spread over its items.",
  items: "What you intend to receive and pay this month. Actual amounts come from assigned transactions and account movements.",
  budgets: "Ceilings for this month. A budget for one thing leaves your plan items to the plan; a budget for everything is the ceiling over both.",
  rates: "Rates used to value future plans. Recorded facts keep the rate frozen at the moment they were posted.",
};

// Planning is the P1 domain (ADR-0019): plan items, budgets and planning FX,
// one view each. The month applies to all three and stays in the heading.
export function PlanningPage({ view, currency, onNotify }: PlanningPageProps) {
  const [offset, setOffset] = useState(0);
  const period = planningMonth(offset);
  const dashboard = useDashboard(currency);
  const planning = usePlanning(dashboard.data?.ids.book, period);
  const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(period.start));

  const data = planning.data;
  // Planning is disabled until the workspace resolves, so a failed dashboard
  // would otherwise leave this query pending for ever and show no error.
  const failed = dashboard.isError || planning.isError || (!dashboard.isPending && !dashboard.data);
  const pending = !failed && (dashboard.isPending || planning.isPending);
  const baseCurrency = data?.book.baseCurrency ?? currency;
  const reportingCurrency = data?.book.reportingCurrency ?? baseCurrency;
  const retry = () => { void dashboard.refetch(); void planning.refetch(); };

  return (
    <section className="planning-page view" aria-labelledby="planning-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Planning</p>
          <h1 id="planning-title">{monthLabel}</h1>
          <p className="lede">{LEDE[view]}</p>
        </div>
        <div className="heading-actions month-nav">
          <button className="secondary-button" type="button" onClick={() => setOffset(offset - 1)} aria-label="Previous month" title="Previous month">‹</button>
          <button className="secondary-button" type="button" onClick={() => setOffset(0)} disabled={offset === 0}>This month</button>
          <button className="secondary-button" type="button" onClick={() => setOffset(offset + 1)} aria-label="Next month" title="Next month">›</button>
        </div>
      </div>

      <SectionTabs tabs={TABS} />

      {pending ? <LoadingPanel message={`Loading ${monthLabel}…`} /> : null}
      {failed ? <ErrorPanel error={planning.error ?? dashboard.error} onRetry={retry} what="planning" /> : null}

      {data && dashboard.data && view === "items" ? (
        <>
          <PlanningSummaryCards summary={data.summary} forecast={data.forecast} />
          <PlanItemsPanel bookID={data.book.id} baseCurrency={baseCurrency} period={period} items={data.planVsActual.items} categories={data.categories} transactions={data.transactions} accounts={dashboard.data.accounts} onNotify={onNotify} />
        </>
      ) : null}

      {data && dashboard.data && view === "groups" ? (
        <PlanGroupsPanel bookID={data.book.id} baseCurrency={baseCurrency}
          items={data.planVsActual.items.map((entry) => entry.item)} period={period}
          categories={data.categories} accounts={dashboard.data.accounts} onNotify={onNotify} />
      ) : null}

      {data && view === "budgets" ? (
        <BudgetsPanel bookID={data.book.id} baseCurrency={baseCurrency} period={period} budgets={data.budgets.budgets} daily={data.daily} categories={data.categories} onNotify={onNotify} />
      ) : null}

      {data && view === "rates" ? (
        <div className="single-column">
          <PlanningFxPanel bookID={data.book.id} baseCurrency={baseCurrency} reportingCurrency={reportingCurrency} rates={data.rates} onNotify={onNotify} />
        </div>
      ) : null}
    </section>
  );
}
